import { useState, useMemo, useCallback, useEffect } from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { z } from 'zod';
import { ArrowLeft, Printer, Loader2 } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  X,
  Building2,
} from 'lucide-react';
import { useCompany } from '@/hooks/use-company';
import { useBranch } from '@/hooks/use-branch';
import {
  getApprovalReport,
  type GetApprovalReportRequest,
  type ReportPeriod,
  type ApprovalCategory,
  type ApprovalStatus,
  type ApprovalReportData,
  type ApprovalLog,
} from '@/services/reports/aprovals';
import type { EmployeeListEmployee } from '@/services/employee';
import {
  ApprovalReportPageHeader,
  FilterControls,
  ApprovalReportTabbedContent,
  ApprovalReportPrintView,
} from '@/components/company/reports/aprovals';
import type { PeriodSelection } from '@/components/company/reports/aprovals/filters/period-range-picker';

const searchSchema = z.object({
  period: z.string().optional().catch('this_month'),
});

export const Route = createFileRoute('/(app)/company/reports/aprovals/')({
  component: ApprovalReportsPage,
  validateSearch: searchSchema,
});

function ApprovalReportsPage() {
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const { selectedCompanyId } = useCompany();
  const { selectedBranchId } = useBranch(selectedCompanyId);

  // Tab state
  const [activeTab, setActiveTab] = useState<'analytics' | 'data'>('analytics');

  // Filter state
  const [period, setPeriod] = useState<ReportPeriod>((search.period as ReportPeriod) || 'this_month');
  const [logsPage, setLogsPage] = useState(1);
  const [selectedEmployee, setSelectedEmployee] = useState<EmployeeListEmployee | null>(null);
  const [filterCategories, setFilterCategories] = useState<ApprovalCategory[]>([]);
  const [filterStatuses, setFilterStatuses] = useState<ApprovalStatus[]>([]);

  // Custom date state (only used when period === 'custom')
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');

  // Build base request params (without include parameter)
  const baseParams = useMemo(() => {
    if (!selectedCompanyId) return null;

    const start_date = period === 'custom' ? customStartDate : undefined;
    const end_date = period === 'custom' ? customEndDate : undefined;

    const params: Omit<GetApprovalReportRequest, 'page' | 'limit' | 'include'> = {
      company_id: selectedCompanyId,
      branch_id: selectedBranchId ?? -1,
      period,
      ...(start_date && end_date && { start_date, end_date }),
      ...(selectedEmployee && { employee_id: selectedEmployee.id }),
      ...(filterCategories.length > 0 && { filter_category: filterCategories }),
      ...(filterStatuses.length > 0 && { filter_status: filterStatuses }),
    };

    return params;
  }, [
    selectedCompanyId,
    selectedBranchId,
    period,
    selectedEmployee,
    customStartDate,
    customEndDate,
    filterCategories,
    filterStatuses,
  ]);

  /** Avoid calling approval-report with period=custom but no dates. */
  const approvalReportQueryReady = useMemo(() => {
    if (period === 'custom') {
      return Boolean(customStartDate.trim() && customEndDate.trim());
    }
    return true;
  }, [period, customStartDate, customEndDate]);

  // Initial load query - fetches both stats and logs (no include parameter)
  // This runs only on page 1 to get all data at once
  const {
    data: initialResponse,
    isLoading: isInitialLoading,
    isError: isInitialError,
    error: initialError,
  } = useQuery({
    queryKey: ['approval-report-initial', baseParams],
    queryFn: () => getApprovalReport({ ...baseParams!, page: 1 }),
    enabled: !!baseParams && logsPage === 1 && approvalReportQueryReady,
    staleTime: 5 * 60 * 1000, // Consider data fresh for 5 minutes
  });

  // Pagination query - only fetches logs (with include=logs)
  // This runs on page 2+ to avoid recomputing stats
  const {
    data: paginationResponse,
    isLoading: isPaginationLoading,
    isError: isPaginationError,
    error: paginationError,
  } = useQuery({
    queryKey: ['approval-report-logs', baseParams, logsPage],
    queryFn: () => getApprovalReport({ ...baseParams!, include: 'logs', page: logsPage }),
    enabled: !!baseParams && logsPage > 1 && approvalReportQueryReady,
    staleTime: 5 * 60 * 1000, // Consider data fresh for 5 minutes
  });

  // Merge data intelligently based on page
  const reportData: ApprovalReportData | undefined = useMemo(() => logsPage === 1
    ? initialResponse?.data
    : (initialResponse?.data && paginationResponse?.data) ? {
        // Keep stats from initial load
        ...initialResponse.data,
        // Update logs with paginated data
        logs: paginationResponse.data.logs,
        logs_pagination: paginationResponse.data.logs_pagination,
        logs_count: paginationResponse.data.logs_count,
      } as ApprovalReportData
    : initialResponse?.data, [logsPage, initialResponse, paginationResponse]); // Fallback to initial data if pagination hasn't loaded yet

  // Loading and error states
  const isLoading = logsPage === 1 ? isInitialLoading : isPaginationLoading;
  const isError = logsPage === 1 ? isInitialError : isPaginationError;
  const error = logsPage === 1 ? initialError : paginationError;

  // Handlers
  const handlePeriodChange = (next: PeriodSelection) => {
    setPeriod(next.period);
    setCustomStartDate(next.startDate);
    setCustomEndDate(next.endDate);
    setLogsPage(1);
    navigate({ search: { period: next.period }, replace: true });
  };

  const handleEmployeeChange = (employee: EmployeeListEmployee | null) => {
    setLogsPage(1);
    setSelectedEmployee(employee);
  };

  // Print state
  const [isPrintLoading, setIsPrintLoading] = useState(false);
  const [isPrinting, setIsPrinting] = useState(false);
  const [allLogs, setAllLogs] = useState<ApprovalLog[] | null>(null);

  // Reset print mode after the browser print dialog closes
  useEffect(() => {
    const handleAfterPrint = () => {
      setIsPrinting(false);
      setAllLogs(null);
    };
    window.addEventListener('afterprint', handleAfterPrint);
    return () => window.removeEventListener('afterprint', handleAfterPrint);
  }, []);

  // Fetch all pages then trigger print
  const handlePrint = useCallback(async () => {
    if (!baseParams || !reportData || !approvalReportQueryReady) return;

    setIsPrintLoading(true);
    try {
      // Fetch the first page to get total_pages info
      const firstPage = await getApprovalReport({ ...baseParams, page: 1, limit: 100 });
      const totalPages = firstPage.data.logs_pagination.total_pages;
      let collected = [...firstPage.data.logs];

      // Fetch remaining pages
      for (let page = 2; page <= totalPages; page++) {
        const res = await getApprovalReport({
          ...baseParams,
          include: 'logs',
          page,
          limit: 100,
        });
        collected = [...collected, ...res.data.logs];
      }

      setAllLogs(collected);
      setIsPrinting(true);

      // Wait for the print view (including charts) to fully render
      requestAnimationFrame(() => {
        setTimeout(() => {
          window.print();
        }, 500);
      });
    } catch (err) {
      console.error('Failed to fetch data for printing:', err);
    } finally {
      setIsPrintLoading(false);
    }
  }, [baseParams, reportData, approvalReportQueryReady]);

  // Check if no company selected
  if (!selectedCompanyId) {
    return (
      <div className="space-y-6">
        {/* Back Button */}
        <div className="flex items-center justify-between gap-4">
          <Button asChild variant="outline" size="sm">
            <Link to="/company/reports">
              <ArrowLeft className="mr-2 size-4" />
              Back to Reports
            </Link>
          </Button>
        </div>
        <Card>
          <CardContent className="py-16">
            <div className="text-center">
              <Building2 className="mx-auto size-12 text-muted-foreground/50" />
              <p className="mt-4 text-lg font-medium">No Company Selected</p>
              <p className="mt-1 text-sm text-muted-foreground">
                Please select a company to view the approval report
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Check for error state
  if (isError) {
    return (
      <div className="space-y-6">
        {/* Back Button */}
        <div className="flex items-center justify-between gap-4">
          <Button asChild variant="outline" size="sm">
            <Link to="/company/reports">
              <ArrowLeft className="mr-2 size-4" />
              Back to Reports
            </Link>
          </Button>
        </div>
        <ApprovalReportPageHeader summary={undefined} />
        <Card>
          <CardContent className="py-16">
            <div className="text-center">
              <X className="mx-auto size-12 text-destructive" />
              <p className="mt-4 text-lg font-medium">Failed to load report</p>
              <p className="mt-1 text-sm text-muted-foreground">
                {error instanceof Error ? error.message : 'An error occurred'}
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Back Button + Print Button */}
      <div className="flex items-center justify-between gap-4 no-print">
        <Button asChild variant="outline" size="sm">
          <Link to="/company/reports">
            <ArrowLeft className="mr-2 size-4" />
            Back to Reports
          </Link>
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={handlePrint}
          disabled={isPrintLoading || !reportData || !approvalReportQueryReady}
        >
          {isPrintLoading ? (
            <Loader2 className="mr-2 size-4 animate-spin" />
          ) : (
            <Printer className="mr-2 size-4" />
          )}
          {isPrintLoading ? 'Preparing...' : 'Print Report'}
        </Button>
      </div>

      {/* Header with Stats */}
      <div className="no-print">
        <ApprovalReportPageHeader summary={reportData?.summary} />
      </div>

      {/* Tab Toggle and Filter Controls Row */}
      <div className="no-print">
        <Tabs value={activeTab} onValueChange={(value) => setActiveTab(value as 'analytics' | 'data')}>
          <div className="flex items-center justify-between gap-4">
            {/* Tab Toggle (Left) */}
            <TabsList>
              <TabsTrigger value="analytics">Analytics</TabsTrigger>
              <TabsTrigger value="data">Data</TabsTrigger>
            </TabsList>

            {/* Filter Controls (Right) */}
            <FilterControls
              period={period}
              selectedEmployee={selectedEmployee}
              filterCategories={filterCategories}
              filterStatuses={filterStatuses}
              customStartDate={customStartDate}
              customEndDate={customEndDate}
              onPeriodChange={handlePeriodChange}
              onEmployeeChange={handleEmployeeChange}
              onCategoriesChange={setFilterCategories}
              onStatusesChange={setFilterStatuses}
            />
          </div>

          {/* Tabbed Content */}
          <div className="mt-6">
            {period === 'custom' && !approvalReportQueryReady ? (
              <Card>
                <CardContent className="py-16">
                  <p className="text-center text-sm text-muted-foreground">
                    Select a date range in the period filter above to load the report.
                  </p>
                </CardContent>
              </Card>
            ) : (
              <ApprovalReportTabbedContent
                data={reportData}
                isLoading={isLoading}
                currentPage={logsPage}
                onPageChange={setLogsPage}
                activeTab={activeTab}
              />
            )}
          </div>
        </Tabs>
      </div>

      {/* Print View - hidden on screen, visible only when printing */}
      {isPrinting && reportData && allLogs && (
        <ApprovalReportPrintView data={reportData} allLogs={allLogs} />
      )}
    </div>
  );
}
